import { Link } from "react-router-dom";
import { BETA_URL } from "../config";

// The marketing landing. Sells the moat ("screenshots never leave your PC, by
// construction") + the two markets (parental + accountability) + the early-beta
// CTA. Light, calm, confident — restraint is the pitch.
export function Landing() {
  return (
    <main>
      {/* ---- Hero ---------------------------------------------------------- */}
      <section className="hero">
        <div className="wrap">
          <div className="rise rise-1">
            <span className="eyebrow">Early beta · Private monitoring for Windows</span>
          </div>
          <h1 className="rise rise-1">
            It watches the screen. It <span className="accent">never</span>{" "}
            leaves the machine.
          </h1>
          <p className="hero-sub rise rise-2">
            KinSafe runs on a family PC, notices on-screen explicit content, and
            sends a clear heads-up — while the screenshots and the graphic
            details stay on that computer. Not stored in our cloud. Not even
            visible to us. By construction, not by promise.
          </p>
          <div className="hero-cta rise rise-3">
            <Link className="btn btn-primary" to={BETA_URL}>
              Click here to join the waitlist
            </Link>
            <Link className="btn btn-ghost" to="/demo">
              Try the sliders first →
            </Link>
          </div>
          <p className="hero-note rise rise-3">
            We&apos;re looking for beta testers. No screen-sharing. No image
            uploads. Works on an old laptop or a gaming rig.
          </p>

          {/* The moat, stated as a three-cell construction diagram. */}
          <div className="moat-strip rise rise-4">
            <div className="moat-cell">
              <div className="k">Stays on the PC</div>
              <div className="v">Screenshots</div>
              <div className="d">
                Captured, scanned, blurred and stored locally. They never reach
                our servers.
              </div>
            </div>
            <div className="moat-cell">
              <div className="k">Leaves the PC</div>
              <div className="v">A short alert</div>
              <div className="d">
                Just the text heads-up to your Discord or email — you choose how
                blunt it is.
              </div>
            </div>
            <div className="moat-cell">
              <div className="k">Seen by us</div>
              <div className="v">Nothing</div>
              <div className="d">
                There is no flagged image in our cloud to leak, subpoena, or
                misuse. It can&apos;t be.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---- The moat explained ------------------------------------------- */}
      <section className="section">
        <div className="wrap">
          <div className="section-head">
            <span className="eyebrow">The difference</span>
            <h2>Most filters phone home. This one can&apos;t.</h2>
            <p>
              Other tools upload what they see to a cloud to classify it — which
              means a copy of the most sensitive moment in someone&apos;s life
              now lives on a company server. KinSafe does the detection on the
              device. When a supervisor taps &ldquo;view,&rdquo; the picture
              streams live from that PC and is never persisted anywhere else.
            </p>
          </div>
          <div className="markets">
            <article className="market">
              <span className="tag">For parents · covert</span>
              <h3>Watch over a child&apos;s PC without the creep factor</h3>
              <p>
                Set how sensitive it is — from catching swimwear and suggestive
                content to explicit-only — and how bluntly it describes what it
                found. The child has no account and sees nothing; you get a calm,
                specific alert.
              </p>
              <ul>
                <li>Per-device sensitivity and graphicness sliders</li>
                <li>Alerts to Discord or email — no image in the message</li>
                <li>Originals auto-delete on the device; nothing in our cloud</li>
              </ul>
            </article>
            <article className="market">
              <span className="tag">For accountability · voluntary</span>
              <h3>Recovery and accountability, with real consent</h3>
              <p>
                Someone in recovery installs it on themselves, invites a sponsor
                or spouse, and can see their own activity — but cannot quietly
                disable monitoring or delete the evidence. Designed for the
                people who actually want the guardrail.
              </p>
              <ul>
                <li>Subject can see their own activity, transparently</li>
                <li>Cannot disable monitoring or wipe evidence</li>
                <li>A sponsor reviews and responds to each alert</li>
              </ul>
            </article>
          </div>
        </div>
      </section>

      {/* ---- Try-before-install funnel ------------------------------------ */}
      <section className="section">
        <div className="wrap">
          <div className="section-head">
            <span className="eyebrow">See it before you install</span>
            <h2>Three ways to kick the tires, no sign-up.</h2>
          </div>
          <div className="markets">
            <article className="market">
              <span className="tag">Slider playground</span>
              <h3>Watch a gallery flag and un-flag live</h3>
              <p>
                Drag the same sliders the product ships and watch a curated
                gallery reclassify in real time — see exactly where your line
                would sit.
              </p>
              <ul>
                <li>
                  <Link to="/demo">Open the slider demo →</Link>
                </li>
              </ul>
            </article>
            <article className="market">
              <span className="tag">Grade your PC</span>
              <h3>Will it run well here?</h3>
              <p>
                A quick in-browser read of your graphics, memory and CPU tells
                you your tier and the speed to expect — before downloading
                anything.
              </p>
              <ul>
                <li>
                  <Link to="/grade">Grade your system →</Link>
                </li>
              </ul>
            </article>
            <article className="market">
              <span className="tag">Hardware load</span>
              <h3>How hard does it work the machine?</h3>
              <p>
                Real per-leg timings from a ≈$150 mini-PC up to a gaming rig, so
                you know what to expect on the computer you actually have.
              </p>
              <ul>
                <li>
                  <Link to="/hardware">See the comparison →</Link>
                </li>
              </ul>
            </article>
          </div>
        </div>
      </section>

      {/* ---- Final CTA ----------------------------------------------------- */}
      <section className="cta-banner">
        <div className="wrap">
          <h2>Help shape the early beta.</h2>
          <p>
            We&apos;re looking for families and accountability partners to test
            KinSafe on real hardware. No screenshots ever leave your machines.
          </p>
          <div className="hero-cta">
            <Link className="btn btn-primary" to={BETA_URL}>
              Click here to join the waitlist
            </Link>
            <Link className="btn btn-ghost" to="/demo">
              Try the sliders first
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
